import React, { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { expenseInvoiceApi } from "../../../assets/apis";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import toast from "react-hot-toast";
import { useReactToPrint } from "react-to-print";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectStyle } from "../../../assets/selectCustomStyle";
import ConfirmPortal from "../../../uiModals/confirmModel/ConfirmPortal";
import {
  setSiderBarActiveLink,
  setSiderBarActiveList,
} from "../../../redux-store/sidebarActiveList";
import Normal from "../../../printsLayouts/Expense/Normal/Normal";
import Thermal from "../../../printsLayouts/Expense/Thermal/Thermal";
import { setExpenseInvoiceData } from "../../../redux-store/invoice";
import {
  dateFormateDDMMYYYY,
  formatNumberWithIndianCommas,
  hasPermission,
} from "../../../utils/helpers";
import { useFinancialYear } from "../../../assets/Date";
export const fetchExpenseInvoices = async () => {
  const response = await axios.get(expenseInvoiceApi);
  return response.data;
};

const Expense = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const printRef = useRef();
  const { firstDayOfYear, lastDayOfYear, todayDate } = useFinancialYear();
  const [startDownloading, setStartDownloading] = useState(false);
  const { ExpenseInvoiceData } = useSelector((store) => store.Invoice);
  const { companyData } = useSelector((state) => state.Company);
  const { FYear } = useSelector((store) => store.FinancialYear);
  const { loggedUser } = useSelector((store) => store.Auth);
  const [openPortal, setOpenPortal] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isCancel, setIsCancel] = useState(null);

  const [allExpenseInvoices, setAllExpenseInvoices] = useState([]);
  const [filters, setFilters] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
  });

  const [queryParams, setQueryParams] = useState({ page: 1, pageSize: 10 });

  // delete invoice login
  const deleteExpenseInvoice = async (id) => {
    handleClosePortal();
    try {
      await axios.delete(`${expenseInvoiceApi}/singleinv/${id}`);
      toast.success("Expense Invoice deleted successfully");
      refetch();
    } catch (error) {
      if (error?.response?.status === 403) return navigate("/upgrade");
      handleApiError(error);
    }
  };

  const handleCancel = async (id) => {
    try {
      await axios.patch(`${expenseInvoiceApi}/cancel_purchase_invoice/${id}`, {
        is_cancelled: true,
      });
      toast.success("Expense invoice cancelled successfully");
      refetch();
    } catch (error) {
      if (error?.response?.status === 403) return navigate("/upgrade");
      if (error.response?.data?.message)
        return toast.error(error.response?.data?.message);
      else toast.error("something went wrong ");
    } finally {
      handleClosePortal();
    }
  };

  const onClickPdfHandler = (data) => {
    dispatch(setExpenseInvoiceData(data));
  };

  const printBillHandler = useReactToPrint({
    content: () => printRef.current,
    documentTitle: companyData?.purchase_inv_prefix
      ? `${companyData?.purchase_inv_prefix}${ExpenseInvoiceData?.invoice}`
      : `EXP-${ExpenseInvoiceData?.invoice}`,
    onBeforeGetContent: () => {},
    onBeforePrint: () => {},
    onAfterPrint: () => {
      dispatch(setExpenseInvoiceData(null));
    },
    onPrintError: () => {},
  });

  useEffect(() => {
    if (ExpenseInvoiceData) {
      printBillHandler();
    }
  }, [ExpenseInvoiceData]);

  const handleApiError = (error) => {
    const errorMessage = error.response?.data?.message || "An error occurred";
    toast.error(errorMessage);
  };

  // Function to close the modal
  const handleOpenPortal = (data) => {
    setDeletingItem(data);
    setOpenPortal(true);
  };

  const handleOpenIsCancelPortal = (data) => {
    setIsCancel(data);
    setOpenPortal(true);
  };

  const handleClosePortal = () => {
    setDeletingItem(null);
    setIsCancel(null);
    setOpenPortal(false);
  };

  // get expense invoice logic
  const fetchExpenseInvoices = async ({ queryKey }) => {
    const [, queryParamsData] = queryKey;
    const queryParams = new URLSearchParams(queryParamsData);
    try {
      const response = await axios.get(`${expenseInvoiceApi}?${queryParams}`);
      const { purchaseInvoices, totalPages } = response.data;
      setAllExpenseInvoices(purchaseInvoices || []);
      setTotalPages(totalPages || 1);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 403) return navigate("/upgrade");
      handleApiError(error);
    }
  };

  const { isLoading, isError, data, error, refetch } = useQuery({
    queryKey: ["purchaseInvoices", queryParams],
    queryFn: fetchExpenseInvoices,
    retry: false,
  });

  //filter search logic
  const onChangeFilterHandler = (e) => {
    const { name, value } = e.target;
    setFilters((pre) => ({ ...pre, [name]: value }));
  };

  const onChangeFilterHandlerWithCondition = (e, validationRegex) => {
    const { name, value } = e.target;
    if (value === "" || validationRegex.test(value)) {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleInputChangeWithConditonTypeNumber = (e, validationRegex) => {
    const { name, value } = e.target;
    if (value === "" || validationRegex.test(value)) {
      setFilters((prev) => ({ ...prev, [name]: Number(value) }));
    }
  };

  const onSearchHandler = (e) => {
    e.preventDefault();
    const { startDate, endDate } = filters;
    if (startDate && endDate && startDate > endDate)
      return toast.error("Start date must be less than or equal to end date");
    setQueryParams((pre) => ({ ...pre, ...filters, ...pagination }));
  };

  const onResetHandler = () => {
    setFilters({});
    setQueryParams((pre) => ({ page: 1, pageSize: 10 }));
    setPagination({ page: 1, pageSize: 10 });
  };

  const handleDownloadAll = async () => {
    setStartDownloading(true);
    try {
      // Make sure to set the responseType to 'blob' to handle the PDF file
      const response = await axios.get(`${expenseInvoiceApi}/download_all`, {
        responseType: "blob", // Specify the response type as 'blob' to get the file
      });
      const currentDate = new Date();
      const formattedDate = `${currentDate
        .getDate()
        .toString()
        .padStart(2, "0")}-${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${currentDate.getFullYear()}`;

      const fileName = `expense_invoices_${formattedDate}.pdf`;
      // Create a download link for the file
      const url = window.URL.createObjectURL(new Blob([response.data])); // Use the Blob object for PDF content
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName); // Set the file name for the downloaded PDF
      document.body.appendChild(link);
      link.click(); // Trigger the download
      link.remove(); // Remove the link element after download
    } catch (error) {
      if (error?.response?.status === 404)
        return toast.error("No expense available");
      toast.error("Please try again");
    } finally {
      setStartDownloading(false);
    }
  };

  useEffect(() => {
    setQueryParams((pre) => ({ ...pre, ...pagination }));
  }, [pagination.page, pagination.pageSize, pagination]);

  useEffect(() => {
    setFilters({ startDate: firstDayOfYear, endDate: todayDate });
    setQueryParams((pre) => ({
      page: 1,
      pageSize: 10,
      startDate: firstDayOfYear,
      endDate: todayDate,
    }));
    setPagination({ page: 1, pageSize: 10 });
  }, [FYear.value]);

  useEffect(() => {
    dispatch(setSiderBarActiveList({ name: "Invoices" }));
    dispatch(setSiderBarActiveLink({ link: "expenses" }));
  }, []);

  useEffect(() => {
    if (isError && error?.response?.status === 403) {
      navigate("/upgrade");
    }
  }, [isError, error, navigate]);

  return (
    <div className="main-panel  ">
      <div className="content-wrapper">
        <div id="first" className="card col-lg-12 p-3">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className="card-title">
              {" "}
              All Expense Invoices{" "}
              <i
                className="fa fa-info-circle text-secondary hover-cursor"
                data-toggle="tooltip"
                title="Prepare your Expense summary"
                aria-hidden="true"
                data-original-title="Contacts who can become our Future clients"
              ></i>
            </div>
            {hasPermission(loggedUser, 34) && (
              <button
                className="btn btn-primary d-flex justify-content-center align-items-center px-2 py-1"
                onClick={() => navigate("/invoices/add/expenses")}
              >
                {/* <i className="mdi  mdi-receipt me-1"></i> */}
                Add Expense
              </button>
            )}
          </div>
          <form onSubmit={onSearchHandler}>
            <div className="row">
              <div className="col-lg-3">
                <div className="form-group">
                  <label className="form-control-label" htmlFor="name">
                    Supplier Name
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-alternative"
                    name="name"
                    id="name"
                    value={filters.name || ""}
                    onChange={(e) =>
                      onChangeFilterHandlerWithCondition(e, /^[\s\S]*$/)
                    }
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <div className="form-group">
                  <label className="form-control-label" htmlFor="invoice">
                    Invoice Number
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-alternative"
                    name="invoice"
                    id="invoice"
                    value={filters.invoice || ""}
                    onChange={(e) =>
                      handleInputChangeWithConditonTypeNumber(e, /^[0-9\b]+$/)
                    }
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <div className="form-group">
                  <label className="form-control-label" htmlFor="startDate">
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="form-control form-control-alternative"
                    name="startDate"
                    min={firstDayOfYear}
                    max={todayDate}
                    id="startDate"
                    value={filters.startDate || ""}
                    onChange={onChangeFilterHandler}
                  />
                </div>
              </div>

              <div className="col-lg-3">
                <div className="form-group">
                  <label className="form-control-label" htmlFor="endDate">
                    End Date
                  </label>
                  <input
                    type="date"
                    className="form-control form-control-alternative"
                    min={filters.startDate || firstDayOfYear}
                    max={todayDate}
                    name="endDate"
                    id="endDate"
                    value={filters.endDate || ""}
                    onChange={onChangeFilterHandler}
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-3">
                <div className="form-group">
                  <label
                    className="form-control-label"
                    htmlFor="payment_status"
                  >
                    Status
                  </label>
                  <select
                    className="form-control form-control-alternative"
                    name="payment_status"
                    id="payment_status"
                    value={filters.payment_status || ""}
                    onChange={onChangeFilterHandler}
                    style={selectStyle}
                  >
                    <option value="">-&nbsp;-select-&nbsp;-</option>
                    <option value="Paid">Paid</option>
                    <option value="Unpaid">Unpaid</option>
                    <option value="Partial">Partial</option>
                  </select>
                </div>
              </div>

              <div
                className="col-lg-9 text-right"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <div className="form-group mt-1">
                  <button
                    type="submit"
                    id="search"
                    className="btn btn-primary btn-sm mt-4"
                  >
                    search
                  </button>
                </div>
                <div className="form-group mt-1">
                  <button
                    type="button"
                    id="reset"
                    className="btn btn-primary btn-sm mt-4"
                    onClick={onResetHandler}
                  >
                    Reset
                  </button>
                </div>
                <div className="form-group mt-1">
                  <button
                    type="button"
                    id="download_all"
                    className="btn btn-primary btn-sm mt-4"
                    style={{ marginLeft: "auto" }}
                    onClick={handleDownloadAll}
                    disabled={startDownloading}
                  >
                    {startDownloading ? "Please wait ..." : "Download All"}
                  </button>
                </div>
              </div>
            </div>
          </form>
          {/* <div className="pt-2 d-flex">
            <select
              className="p-1"
              value={pagination.pageSize}
              onChange={(e) =>
                setPagination((prev) => {
                  return { ...prev, pageSize: e.target.value };
                })
              }
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="25">25</option>
              <option value="30">30</option>
              <option value="">All</option>
            </select>
            <button
              type="button"
              id="download_all"
              className="btn btn-primary btn-sm"
              style={{ marginLeft: "auto" }}
              onClick={handleDownloadAll}
              disabled={startDownloading}
            >
              {startDownloading ? "Please wait ..." : "Download All"}
            </button>
          </div> */}
          <div className="d-flex flex-column align-items-center datatable">
            <DataTable
              value={allExpenseInvoices}
              style={{ width: "100%" }}
              id="expense-invoice-table"
              className="mytable"
            >
              <Column
                className="border-bottom border-top"
                header="SR. No."
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "4rem",
                }}
                body={(rowData, { rowIndex }) => (
                  <p>
                    {pagination.page * pagination.pageSize -
                      pagination.pageSize +
                      rowIndex +
                      1}
                  </p>
                )}
              />
              <Column
                className="border-bottom border-top"
                field="invoice"
                header="INVOICE"
                body={(rowData) =>
                  companyData?.purchase_inv_prefix
                    ? `${companyData.purchase_inv_prefix}${rowData.invoice}`
                    : `EXP-${rowData.invoice}`
                }
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "6rem",
                }}
              />
              <Column
                className="border-bottom border-top"
                field="name"
                header="NAME"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  maxWidth: "8rem",
                  minWidth: "8rem",
                }}
              />
              <Column
                className="border-bottom border-top"
                field="date"
                header="DATE"
                body={(data) => dateFormateDDMMYYYY(data.date)}
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "6rem",
                }}
              />
              {/* <Column
                className="border-bottom border-top"
                field="amount"
                header="AMOUNT"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "6rem",
                }}
              /> */}
              {/* <Column
                className="border-bottom border-top"
                field="total_tax"
                header="TAX"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "6rem",
                }}
              /> */}
              {/* <Column
                className="border-bottom border-top"
                field="discount"
                header="DISCOUNT"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "6rem",
                }}
              /> */}
              <Column
                className="border-bottom border-top"
                field="total"
                header="TOTAL"
                body={(rowData) =>
                  formatNumberWithIndianCommas(rowData.total || 0)
                }
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "6rem",
                }}
              />
              {/* <Column
                className="border-bottom border-top"
                field="payment_status"
                header="STATUS"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "6rem",
                }}
              /> */}
              <Column
                className="border-bottom border-top"
                field="paid_amount"
                body={(data) =>
                  formatNumberWithIndianCommas(data.paid_amount || 0)
                }
                header="PAID"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "6rem",
                }}
              />
              <Column
                className="border-bottom border-top"
                field="balance"
                header="BALANCE"
                body={(data) => formatNumberWithIndianCommas(data.balance || 0)}
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "6rem",
                }}
              />
              <Column
                className="border-bottom border-top"
                field="is_cancelled"
                body={(data) => (
                  <span>
                    {data?.is_cancelled && (
                      <>
                        <span style={{ color: "#ffaf00" }}>Cancelled</span>
                        <br />
                      </>
                    )}
                    {data?.payment_status}
                  </span>
                )}
                header="STATUS"
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "6rem",
                }}
              />
              {/* <Column
                className="border-bottom border-top"
                field="paid_date"
                header="PAID DATE"
                body={(data) => data.paid_date || "-"}
                style={{
                  height: "3rem",
                  alignItems: "center",
                  textAlign: "start",
                  minWidth: "6rem",
                }}
              /> */}
              <Column
                className="border-bottom border-top"
                body={(data) => (
                  <div
                    style={{
                      minWidth: "50%",
                      display: "flex",
                    }}
                  >
                    {data?.is_cancelled
                      ? ""
                      : hasPermission(loggedUser, 40) && (
                          <i
                            style={{ cursor: "pointer" }}
                            className="dropdown-item-icon mdi mdi-credit-card text-primary me-2"
                            title="Add Payment"
                            onClick={() =>
                              navigate(`/invoices/pay/expenses/${data.invoice}`)
                            }
                          ></i>
                        )}

                    <i
                      style={{ cursor: "pointer" }}
                      className="dropdown-item-icon mdi mdi-information text-primary me-2"
                      onClick={(e) => {
                        navigate(`/invoices/view/expenses/${data.invoice}`);
                      }}
                      title="View Invoice"
                    />
                    {data?.is_cancelled ? (
                      ""
                    ) : (
                      <>
                        {data.payment_status !== "Paid" && (
                          <>
                            {hasPermission(loggedUser, 35) && (
                              <i
                                style={{ cursor: "pointer" }}
                                onClick={(e) => {
                                  navigate(
                                    `/invoices/edit/expenses/${data.invoice}`
                                  );
                                }}
                                className="dropdown-item-icon mdi mdi-pen text-primary me-2"
                                title="Edit Invoice"
                              />
                            )}
                            {/* {hasPermission(loggedUser, 36) && (
                          <i
                            style={{ cursor: "pointer" }}
                            onClick={(e) => {
                              handleOpenPortal(data);
                            }}
                            className="dropdown-item-icon mdi mdi-delete-forever text-primary me-2"
                            title="Delete Invoice"
                          />
                        )} */}
                          </>
                        )}
                      </>
                    )}
                    <i
                      style={{ cursor: "pointer" }}
                      className="dropdown-item-icon mdi mdi-printer text-primary me-2"
                      onClick={() => onClickPdfHandler(data)}
                      title="Print"
                    />
                    {data?.is_cancelled ? (
                      ""
                    ) : (
                      <i
                        className="dropdown-item-icon mdi mdi-cancel text-primary me-2 hover-cursor"
                        title="Cancel"
                        onClick={() => handleOpenIsCancelPortal(data)}
                      />
                    )}
                  </div>
                )}
                header="ACTIONS"
              />
            </DataTable>
            <div
              className=""
              style={{
                display: "flex",
                width: "80%",
                justifyContent: "space-evenly",
                margin: "10px 0 0 0",
              }}
            >
              <button
                style={{ border: "none", fontSize: "24px" }}
                disabled={pagination.page === 1}
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    page: prev.page - 1,
                  }))
                }
              >
                <i className="mdi mdi-chevron-left" />
              </button>
              <span>
                Page {pagination.page} of {totalPages}
              </span>
              <button
                style={{ border: "none", fontSize: "24px" }}
                disabled={pagination.page === totalPages}
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }))
                }
              >
                <i className="mdi mdi-chevron-right" />
              </button>
              <select
                value={pagination.pageSize}
                onChange={(e) =>
                  setPagination((prev) => ({
                    ...prev,
                    pageSize: e.target.value,
                  }))
                }
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
                <option value="25">25</option>
                <option value="30">30</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      {openPortal && (
        <ConfirmPortal
          onClose={handleClosePortal}
          type={"Expense Invoice"}
          isCancel={isCancel}
          name={
            companyData?.purchase_inv_prefix
              ? `${companyData?.purchase_inv_prefix}${
                  isCancel ? isCancel?.invoice : deletingItem?.invoice
                }`
              : `EXP-${isCancel ? isCancel?.invoice : deletingItem?.invoice}`
          }
          id={isCancel ? isCancel.id : deletingItem.id}
          handleDalete={deleteExpenseInvoice}
          handleCancel={handleCancel}
        ></ConfirmPortal>
      )}
      {companyData?.pdfLayout === "normal" ? (
        <div style={{ display: "none" }}>
          <Normal printRef={printRef} />
        </div>
      ) : (
        <div style={{ display: "none" }}>
          <Thermal printRef={printRef} />
        </div>
      )}
    </div>
  );
};

export default Expense;
